import React, { useEffect, useRef, useState } from 'react';
import { useFormik } from 'formik';
import classes from './landingCooperation.module.css';
import * as Yup from 'yup';
import Toaster from '../Snackbar';
import PhoneField from '../form/PhoneField';
import Select from 'react-select';
import VerifyPhone from './VerifyPhone';
import { executeRecaptchaEnterprise } from '../../utils/recaptcha';
import { sendCode } from '../HomePage/Hero/HeroUtilities';
import { useSnackbar } from 'notistack';

const features = (discount) => [
  {
    id: 1,
    title: 'خطة تعليمية مخصصة ومصممة على احتياج الطالب',
  },
  {
    id: 2,
    title: 'دروس خصوصية عن بعد وفردية (1-1)',
  },
  {
    id: 3,
    title: 'باقات شهرية مرنة وبأسعار منافسة',
  },
  {
    id: 4,
    title: 'ندرس جميع المراحل من الروضة وحتى الجامعة',
  },
  {
    id: 5,
    title: `خصم مخصص بقيمة ${discount}%`,
  },
];

const levelOptions = [
  { value: 'الروضة', label: 'الروضة' },
  { value: 'الابتدائي', label: 'الابتدائي' },
  { value: 'المتوسط', label: 'المتوسط' },
  { value: 'الثانوي', label: 'الثانوي' },
  { value: 'الجامعة', label: 'الجامعة' },
];

const subjectOptions = [
  { value: 'الانجليزي', label: 'الانجليزي' },
  { value: 'الرياضيات', label: 'الرياضيات' },
  { value: 'الفيزياء', label: 'الفيزياء' },
  { value: 'الكيمياء', label: 'الكيمياء' },
  { value: 'القدرات', label: 'القدرات' },
  { value: 'التحصيلي', label: 'التحصيلي' },
  { value: 'المتابعة', label: 'المتابعة' },
  { value: 'أخرى', label: 'أخرى' },
];

const curriculumOptions = [
  { value: 'سعودي', label: 'سعودي' },
  { value: 'دولي', label: 'دولي' },
];

// International-curriculum catalog (static): the same stages as the Saudi
// list, labeled with their grade range. Grades 1-8 study Science; from
// Grade 9 it splits into Physics/Chemistry/Biology (so المتوسط, which spans
// Grade 7-9, offers both sets). Arabic runs through all grades.
const toOption = (value) => ({ value, label: value });

const intlSubjectsByLevel = {
  'الابتدائي (Grade 1-6)': ['Math', 'English', 'Science', 'Arabic'],
  'المتوسط (Grade 7-9)': [
    'Math',
    'English',
    'Science',
    'Arabic',
    'Physics',
    'Chemistry',
    'Biology',
  ],
  'الثانوي (Grade 10-12)': [
    'Math',
    'English',
    'Physics',
    'Chemistry',
    'Biology',
    'Arabic',
  ],
};

const intlLevelOptions = Object.keys(intlSubjectsByLevel).map(toOption);

// Unique union of the subjects offered for the picked stages (all stages when
// none picked yet), keeping catalog order.
const intlSubjectOptions = (pickedLevels) => {
  const levels = pickedLevels?.length
    ? pickedLevels.map((level) => level.value)
    : Object.keys(intlSubjectsByLevel);
  const subjects = [];
  levels.forEach((level) => {
    (intlSubjectsByLevel[level] || []).forEach((subject) => {
      if (!subjects.includes(subject)) subjects.push(subject);
    });
  });
  return subjects.map(toOption);
};

// react-select paints the selected option's text white by default, which is
// unreadable on this theme's #eee highlight.
const selectStyles = {
  option: (base) => ({ ...base, color: '#01202b' }),
};

const theme = {
  colors: {
    primary: '#eee',
    primary25: '#eee',
    primary50: '#eee',
    primary75: '#eee',
    neutral0: '#fff',
    neutral5: '#fff',
    neutral10: '#eee',
    neutral20: '#a5a5a5',
    neutral30: '#ff693d',
    neutral40: '#ff693d',
    neutral50: '#888',
    neutral60: '#000',
    neutral70: '#ff693d',
    neutral80: '#000',
    neutral90: '#000',
  },
};
const HeroSection = ({ logo, name, discount, referralCode }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formStep, setFormStep] = useState(1);
  const isStageEnv = process.env.NEXT_PUBLIC_CAPTCHA_PUBLIC_DISABLED === 'true';
  const milliSecondsRef = useRef(0);
  const { enqueueSnackbar } = useSnackbar();
  const formik = useFormik({
    initialValues: {
      name: '',
      mobile: '',
      children_count: '',
      levels: '',
      subjects: '',
      curriculum: curriculumOptions[0],
    },
    validationSchema: Yup.object({
      name: Yup.string()
        .required('الاسم مطلوب')
        .matches(
          /^[a-zA-Z\u0600-\u06FF ]+$/,
          'الاسم يجب أن يحتوي على حروف فقط'
        ),
      mobile: Yup.number()
        .typeError('يسمح بادخال الازقام فقط')
        .required('مطلوب'),
      children_count: Yup.string()
        .required('عدد الأبناء مطلوب')
        .test('len', 'يجب أن يكون عدد الأبناء أكبر من 0', (val) => val > 0),
      levels: Yup.string().required('المراحل الدراسية مطلوبة'),

      subjects: Yup.string().required('المواد الدراسية مطلوبة'),
    }),
    onSubmit: (values) => {
      setIsSubmitting(true);
      sendRegistrantCode();
    },
    validate: (values) => {
      const errors = {};
      if (!values.isMobileValid) {
        errors.mobile = 'الرقم غير صالح';
      }
      return errors;
    },
  });

  const onMobileChange = (isValid, mobile, setFieldValue, other) => {
    setFieldValue('isMobileValid', isValid);
    setFieldValue('mobile', mobile.replace(/\s/g, ''));
  };

  const isIntl = formik.values.curriculum?.value === 'دولي';
  const currentLevelOptions = isIntl ? intlLevelOptions : levelOptions;
  const currentSubjectOptions = isIntl
    ? intlSubjectOptions(formik.values.levels || [])
    : subjectOptions;

  const onCurriculumChange = (value) => {
    formik.setFieldValue('curriculum', value);
    // the level/subject catalogs differ per curriculum — clear stale picks
    formik.setFieldValue('levels', []);
    formik.setFieldValue('subjects', []);
  };

  const onLevelsChange = (value) => {
    formik.setFieldValue('levels', value);
    if (isIntl) {
      const allowed = intlSubjectOptions(value).map((option) => option.value);
      formik.setFieldValue(
        'subjects',
        (formik.values.subjects || []).filter((subject) =>
          allowed.includes(subject.value)
        )
      );
    }
  };

  function sendFormValuesG4A() {
    if (typeof window === 'undefined') return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'form_submit',
      event_category: 'form',
      event_label: 'cooperartion',
      name: formik.values.name,
      mobile: formik.values.mobile,
      children_count: formik.values.children_count,
      levels: formik.values.levels,
      subjects: formik.values.subjects,
      cooperation: name,
    });
  }

  const sendRegistrantCode = async () => {
    try {
      let data;
      sendFormValuesG4A();
      if (isStageEnv) {
        data = {
          ...formik.values,
          phone: formik.values.mobile,
          country_code: 966,
          'g-token': 'g-token-staging',
        };
      } else {
        const token = await executeRecaptchaEnterprise('sms_verification');
        data = {
          ...formik.values,
          phone: formik.values.mobile,
          country_code: 966,
          'g-token': token,
        };
      }

      const res = await sendCode({
        ...data,
        ...(referralCode ? { referral_code: referralCode } : {}),
        subjects: formik.values.subjects
          .map((subject) => subject.value)
          .join('،'),
        levels: formik.values.levels.map((level) => level.value).join('،'),
        curriculum: formik.values.curriculum?.value || 'سعودي',
      });
      if (res.status === true) {
        setFormStep(2);
      } else {
        enqueueSnackbar(res.message, {
          variant: 'error',
          anchorOrigin: { vertical: 'top', horizontal: 'right' },
        });
      }
    } catch (error) {
      enqueueSnackbar('حدث خطأ، يرجى المحاولة مرة أخرى', {
        variant: 'error',
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    const timer = setInterval(() => {
      milliSecondsRef.current += 1;
    }, 1);
    return () => clearInterval(timer);
  });

  return (
    <section className={classes['hero-section']}>
      <div className={classes['hero-content']}>
        <div className={classes['hero-content__form']} id="register-form">
          {formStep === 1 && (
            <form onSubmit={formik.handleSubmit}>
              <div className={classes['form-group']}>
                <input
                  type="text"
                  name="name"
                  id="name"
                  onBlur={formik.handleBlur}
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  placeholder="الاسم"
                />
                {formik.errors.name && formik.touched.name && (
                  <p className={classes['error']}>{formik.errors.name}</p>
                )}
                <div className={classes['phone-field']}>
                  <p className={classes['country-code']}>966+</p>
                  <PhoneField
                    onChange={(isValid, mobile, other) =>
                      onMobileChange(
                        isValid,
                        mobile,
                        formik.setFieldValue,
                        other
                      )
                    }
                    value={formik.values.mobile.replace(/\s/g, '')}
                    onlyCountries={['sa']}
                    defaultCountry="sa"
                    preferredCountries={[]}
                    allowDropdown={false}
                    countryCode="966"
                    placeholder="55 451 1590"
                  />
                </div>
                {formik.errors.mobile && formik.touched.mobile && (
                  <p className={classes['error']}>{formik.errors.mobile}</p>
                )}
                <input
                  type="number"
                  name="children_count"
                  id="children_count"
                  value={formik.values.children_count}
                  onChange={formik.handleChange}
                  placeholder="عدد الأبناء"
                  min="1"
                />
                {formik.errors.children_count &&
                  formik.touched.children_count && (
                    <p className={classes['error']}>
                      {formik.errors.children_count}
                    </p>
                  )}
                <Select
                  name="curriculum"
                  instanceId="curriculum"
                  options={curriculumOptions}
                  className={classes['custom-select']}
                  isRtl={true}
                  theme={theme}
                  styles={selectStyles}
                  isSearchable={false}
                  value={formik.values.curriculum}
                  formatOptionLabel={(option, { context }) =>
                    context === 'value' ? `المنهج: ${option.label}` : option.label
                  }
                  onChange={onCurriculumChange}
                />
                <br />
                <Select
                  name="levels"
                  instanceId="levels"
                  options={currentLevelOptions}
                  isMulti={true}
                  placeholder="المراحل الدراسية"
                  className={classes['custom-select']}
                  isRtl={true}
                  theme={theme}
                  styles={selectStyles}
                  value={formik.values.levels}
                  onChange={onLevelsChange}
                  onKeyDown={(e) => e.preventDefault()}
                />
                {formik.errors.levels && formik.touched.levels && (
                  <p className={classes['error']}>{formik.errors.levels}</p>
                )}
                <br />
                <Select
                  name="subjects"
                  instanceId="subjects"
                  options={currentSubjectOptions}
                  isMulti={true}
                  placeholder="المواد الدراسية"
                  className={classes['custom-select']}
                  isRtl={true}
                  theme={theme}
                  styles={selectStyles}
                  value={formik.values.subjects}
                  onChange={(value) => {
                    formik.setFieldValue('subjects', value);
                  }}
                  delimiter="،"
                  onKeyDown={(e) => e.preventDefault()}
                />
                {formik.errors.subjects && formik.touched.subjects && (
                  <p className={classes['error']}>{formik.errors.subjects}</p>
                )}
              </div>
              <button type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <div className="spinner-border text-light" role="status">
                    <span className="sr-only"></span>
                  </div>
                ) : (
                  <span>إرسال الطلب</span>
                )}
              </button>
            </form>
          )}
          {formStep === 2 && (
            <form className={classes['hero-content__marketing-form']}>
              <VerifyPhone
                countryCode={966}
                setFormStep={setFormStep}
                phone={formik.values.mobile}
                cooperation={name}
                formValues={{
                  ...formik.values,
                  levels:
                    formik.values.levels.length &&
                    formik.values.levels
                      .map((level) => level.value)
                      .reduce((acc, cur) => acc + '،' + cur),
                  subjects:
                    formik.values.subjects.length &&
                    formik.values.subjects
                      .map((subject) => subject.value)
                      .reduce((acc, cur) => acc + '،' + cur),
                  curriculum: formik.values.curriculum?.value || 'سعودي',
                  cooperation_name: name,
                  ...(referralCode ? { referral_code: referralCode } : {}),
                }}
              />
            </form>
          )}
        </div>
        <div className={classes['hero-content__text']}>
          <h1>درس أبنائك مع أفضل المدرسين الخصوصيين في السعودية</h1>
          <div className={classes['hero-logo']}>
            <img src={logo} alt={name} />
          </div>
          <div className={classes['hero-features']}>
            {features(discount).map((feature) => prepareFeatureLine(feature))}
          </div>
        </div>
      </div>
      <div className={classes['register-mobile']}>
        <button
          onClick={() => {
            document
              .getElementById('register-form')
              .scrollIntoView({ behavior: 'smooth' });
          }}
        >
          اشترك الآن
        </button>
      </div>
      <style scoped>
        {`
        .intl-tel-input .flag-container {
          width: unset;
          right: unset;
          left: 8px;
        }
        .intl-tel-input input[type=tel] {
          padding-left: 82px !important;
          font-size: 13px;
        }
        `}
      </style>
    </section>
  );
};

function prepareFeatureLine(feature) {
  return (
    <p key={feature.title}>
      <i className="fas fa-check ml-2"></i>
      {feature.title}
    </p>
  );
}
export default HeroSection;
